/* eslint-disable react/prop-types */
import { Info } from '@mui/icons-material';
import { Autocomplete, Box, Button, Popover, Typography } from '@mui/material';
import useTheme from '@mui/material/styles/useTheme';
import { SWTextField } from 'components/Form/Input/SWTextField';
import { MultiSelector } from 'components/Form/Selectors/MultiSelector';
import { Selector } from 'components/Form/Selectors/Selector';
import { InteractiveModal } from 'components/Modals/InteractiveModal/BaseModal';
import useAppTheme from 'hooks/useAppTheme';
import { ThemePalette } from 'mui.theme';
import { type SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsClonedStatus, updateNewRule } from 'store/rule.slice';
import type { Asset } from 'types/asset.types';
import { type SelectedRule } from 'types/notification.types';
import type { ICreateRulePayload } from 'types/payloads/rule.payload.types';
import type { SelectOptions } from 'types/utils.types';
import type { Role } from 'components/Permission/permission.types';
import { getAssetsCommmonTags } from 'utils/helpers/device';
import { createRuleGeneralInfo } from 'utils/schemas/rule.schema';
import { validateYupSchema } from 'utils/validators/schema.validator';
import {
  getAssetListOptions,
  getAssetMakeOptions,
  getAssetModelOptions,
  getCurrentRuleMakeModel,
  getInitialAssetTypes,
  handleSelectAll,
  handleSelectAssetAll,
  handleSelectAssetSingle,
  handleSelectSingle,
  handleSelectSingleModel,
} from 'views/Settings/Rules/helpers/common';
import { RuleModalHeader } from './RuleModalHeader';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  open: boolean;
  handleAbort: () => void;
  handleConfirm: () => void;
  title: string;
  assets: Asset[];
  newRule: ICreateRulePayload;
  confirmCancellation?: boolean;
  enableDarkTheme: boolean;
}

// Main SensorInfoBox Component
const SensorInfoBox = ({
  selectedAssets,
  assets,
}: {
  selectedAssets: Array<{ id: string; display: string }>;
  assets: Asset[];
}) => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // popover
  const open = Boolean(anchorEl);
  const id = open ? 'sensor-popover' : undefined;

  const sensorsToDisplay = useMemo(() => {
    try {
      const ruleAssets = selectedAssets.reduce((result: Asset[], item) => {
        const matchedDevice = assets.find(
          ({ device }) => device.deviceId === item.id
        );
        if (matchedDevice) {
          result.push(matchedDevice);
        }
        return result;
      }, []);

      const assetTags = getAssetsCommmonTags(ruleAssets);
      return assetTags;
    } catch (error) {
      console.error(error);
      return [];
    }
  }, [selectedAssets, assets]);

  return (
    <div
      style={{
        backgroundColor: '#273344',
        border: '1px solid #334155',
        padding: '1rem',
        borderRadius: '0.375rem',
        color: '#94A3B8',
        fontSize: '0.875rem',
        lineHeight: '1.6',
      }}
    >
      <Box display={'flex'} flexDirection={'row'} gap={1} alignItems={'center'}>
        <Info fontSize="small" />
        <p style={{ margin: 0 }}>
          <strong style={{ color: '#CBD5E1', fontWeight: 600 }}>
            {t('note_on_sensor_availability')}
          </strong>
        </p>
      </Box>

      <p style={{ margin: '0 0 0.5rem 0' }}>
        {`When you select multiple assets, the 'Conditions' screen will only display
        sensors that are`}
        <strong style={{ color: '#CBD5E1', fontWeight: 600 }}>
          {' '}
          {t('common_to_all_your_selections')}
        </strong>
        .
      </p>

      <p style={{ margin: '0 0 0.5rem 0', marginTop: '0.5rem' }}>
        {t('based_on_your_current_selections')}
        <strong style={{ color: '#CBD5E1', fontWeight: 600 }}>
          {' '}
          {sensorsToDisplay.length}
        </strong>{' '}
        {t('sensors_will_be_available')}
      </p>

      <Button
        aria-describedby={id}
        size="small"
        sx={{
          width: '100%',
          height: '100%',
          border: '1px solid #2B53BC',
          borderRadius: '5px',
          py: '4px',
          textTransform: 'none',
        }}
        color={enableDarkTheme ? 'primary' : 'info'}
        onClick={handleClick}
        variant="contained"
      >
        {open ? 'Hide Sensors' : 'View Sensors'}
      </Button>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          style: {
            backgroundColor: '#1E293B',
            border: '1px solid #334155',
            padding: '0.75rem',
            color: '#94A3B8',
            maxHeight: '200px',
            overflowY: 'auto',
          },
        }}
      >
        <strong style={{ color: '#CBD5E1', fontWeight: 600 }}>
          {t('common_sensors')}
        </strong>
        <ul
          style={{
            listStyleType: 'disc',
            paddingLeft: '1.25rem',
            marginTop: '0.5rem',
            color: '#94A3B8',
          }}
        >
          {sensorsToDisplay.length > 0 ? (
            sensorsToDisplay.map((sensor, index) => (
              <li key={index} style={{ marginBottom: '0.25rem' }}>
                {sensor.display}
              </li>
            ))
          ) : (
            <li>{t('no_sensors_found_for_the_current_selection')}</li>
          )}
        </ul>
      </Popover>
    </div>
  );
};

/**
 *
 * The createRuleGeneralInfo modal is a modal
 * that is used to create a rule general info.
 */
export const CreateRuleGeneralInfoModal = ({
  open,
  handleAbort,
  handleConfirm,
  title,
  assets,
  newRule,
  confirmCancellation = false,
  enableDarkTheme,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const { typography } = useTheme();
  const dispatch = useAppDispatch();
  const { rules } = useAppSelector((state) => state.ruleReducer);
  const userRole = useAppSelector((state) => state.authReducer.role) as Role;
  const privilegedRoles: Role[] = ['director', 'internal-user'];
  const isPrivilegedUser = privilegedRoles.includes(userRole);

  // Options for rule type and feature type selectors (privileged users only)
  const ruleTypeOptions: SelectOptions[] = [
    { id: 'internal', display: 'Internal' },
    { id: 'user-defined', display: 'User Defined' },
  ];
  const featureTypeOptions: SelectOptions[] = [
    { id: 'ml-insights', display: 'ML Insights' },
    { id: 'tpms', display: 'TPMS' },
  ];
  const [assetSelectOptions, setAssetSelectOptions] = useState<SelectOptions[]>(
    []
  );

  const [assetTypeOptions] = useState<SelectOptions[]>(
    getInitialAssetTypes(assets)
  );
  const [assetMakeOptions, setAssetMakeOptions] = useState<SelectOptions[]>([]);
  const [assetModelOptions, setAssetModelOptions] = useState<SelectOptions[]>(
    []
  );

  const [currentRule, setCurrentRule] = useState<ICreateRulePayload>(newRule);
  const [isRuleNameError, setIsRuleNameError] = useState({
    length: false,
    duplicate: false,
  });
  const [isValidForm, setFormValidity] = useState(false);
  const [assetFilter, setAssetFilter] = useState<{
    assetType: string;
    assetMake: SelectOptions[];
    assetModel: SelectOptions[];
  }>({
    assetType: '',
    assetMake: [],
    assetModel: [],
  });

  // const { make, model } = getCurrentRuleMakeModel(currentRule.assets, assets);
  const { make, model } = useMemo(
    () => getCurrentRuleMakeModel(currentRule.assets, assets),
    [currentRule.assets, assets]
  );

  const { assetType, assetMake, assetModel } = assetFilter;
  // const [modelChanged, setModelChanged] = useState(model.length);
  const savedRules = rules
    .filter((rule) => !rule.isDeleted) // Filter out the deleted rules
    .map((rule) => ({ ...rule }));
  const [selectedRule, setSelectedRule] = useState<SelectedRule>();

  const assetDetails = (): Asset | undefined => {
    const selectedAsset = selectedRule?.deviceIds ?? [];
    if (selectedAsset?.length > 0 || newRule.assets.length > 0) {
      return assets.find(
        (asset) =>
          asset.device.deviceId === (selectedAsset[0] || newRule.assets[0].id)
      );
    }
  };
  const selectedAssetDetailType = (value: any, param: keyof Asset) => {
    if (value) {
      return value.toString();
    } else {
      const details = assetDetails();
      if (details && param) {
        return details[param].toString();
      }
    }
  };

  /**
   * This UseEffect Updates the asset make option.
   * Determined by the assetType Selection
   */
  useEffect(() => {
    const makeOptions = getAssetMakeOptions(
      assets,
      selectedAssetDetailType(assetType, 'assetType')
    );
    const modelOptions = getAssetModelOptions(
      assets,
      selectedAssetDetailType(assetType, 'assetType'),
      makeOptions
    );
    setAssetMakeOptions(makeOptions);
    const initialSelectedItems = make && make.length > 0 ? make : makeOptions;
    setAssetFilter((prevFilter) => ({
      ...prevFilter,
      assetMake: initialSelectedItems,
      assetModel: modelOptions,
    }));
  }, [assetType, selectedRule]);

  /**
   * This UseEffect Updates the asset model option.
   * Determined by the assetType and assetMake Selections
   */

  useEffect(() => {
    if (assetMake.length < 1) {
      setAssetModelOptions([]);
    } else {
      const modelOptions = getAssetModelOptions(
        assets,
        selectedAssetDetailType(assetType, 'assetType'),
        assetMake
      );
      setAssetModelOptions(modelOptions);
      const initialSelectedItems =
        model && model.length > 0 ? model : modelOptions;

      setAssetFilter((prevFilter) => ({
        ...prevFilter,
        assetModel: initialSelectedItems,
      }));
    }
  }, [assetMake]);

  /**
   * This UseEffect Updates the asset list option.
   * Determined by the assetModel Selections
   */

  useEffect(() => {
    if (assetModel.length < 1) {
      setAssetSelectOptions([]);
    } else {
      const listOptions = getAssetListOptions(
        assets,
        selectedAssetDetailType(assetType, 'assetType'),
        assetModel
      );

      //  to add sensor info
      const updatedListOptionsWithSensorData = listOptions?.map((element) => {
        const deviceIdArr = [element.id]; // adding device id in array
        const tempRuleAssets = deviceIdArr.reduce((result: Asset[], item) => {
          const matchedDevice = assets.find(
            ({ device }) => device.deviceId === item
          );
          if (matchedDevice) {
            result.push(matchedDevice);
          }
          return result;
        }, []);
        const assetTags = getAssetsCommmonTags(tempRuleAssets);
        return {
          ...element,
          display: `${element.display} (${assetTags.length} sensors)`,
        };
      });

      setAssetSelectOptions(updatedListOptionsWithSensorData);
    }
  }, [assetModel, model]);

  /**
   * This UseEffect Validates the form as user fills out fields
   */
  useEffect(() => {
    const validateForm = async () =>
      await validateYupSchema(currentRule, createRuleGeneralInfo);

    void validateForm().then((isValid) => {
      setFormValidity(isValid);
    });
  }, [currentRule]);

  /**
   * This UseEffect Updates the asset selection optioins.
   * Determined by the asset Filter attributes
   */
  useEffect(() => {
    if (assetMake.length < 1 || assetModel.length < 1) {
      setAssetSelectOptions([]);
    } else {
      const mappedAsset: SelectOptions[] = [];
      let filteredAssets = assets.filter(
        (asset) => asset.assetType === assetType
      );

      if (assetMake.length) {
        const assetMakeList = assetMake.map((assetMake) => {
          return assetMake.id;
        });
        filteredAssets = filteredAssets.filter((asset) =>
          assetMakeList.includes(asset.make)
        );
      }

      if (assetModel.length) {
        const assetModelList = assetModel.map((assetModel) => {
          return assetModel.id;
        });
        filteredAssets = filteredAssets.filter((asset) =>
          assetModelList.includes(asset.model)
        );
      }
      /**
       * Using the asset device Id as the id here since
       * we are saving the deviceID as the device list in the DB.
       */
      filteredAssets.forEach((asset) => {
        mappedAsset.push({
          id: asset.device.deviceId,
          display: asset.bumperNumber,
        });
      });

      setCurrentRule({
        ...currentRule,
        assets: mappedAsset.length > 0 ? [...mappedAsset] : currentRule.assets,
      });
    }
  }, [assets, assetFilter]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setIsRuleNameError({ duplicate: false, length: false });
    value.length > 40
      ? setIsRuleNameError({ ...isRuleNameError, length: true })
      : setIsRuleNameError({ duplicate: false, length: false });
    setCurrentRule({
      ...currentRule,
      [name]: value,
    });
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    value.length > 40
      ? setIsRuleNameError({ ...isRuleNameError, length: true })
      : setIsRuleNameError({ duplicate: false, length: false });
    if (rules.some((rule) => rule.ruleName === value)) {
      setIsRuleNameError({ ...isRuleNameError, duplicate: true });
    }
  };

  const handleSelect = async (
    event: SyntheticEvent<Element, Event>,
    value: string | SelectedRule | null
  ) => {
    if (value && typeof value === 'object') {
      // to only return if device id is there and matching asset id is there for selection else do nothing
      const selectedAssets =
        (value?.deviceIds ?? value?.devices)?.flatMap((device) => {
          const bumperNo = assets.find(
            (asset) => asset.device.deviceId === device
          );

          if (!bumperNo) return []; // don't include if not found

          return { id: device, display: bumperNo.bumperNumber ?? '' };
        }) ?? [];

      const adaptedArray = value.distributionUsers.map((item: any) => {
        if (typeof item === 'object' && item !== null) {
          return {
            id: item.id,
            display: item.id,
          };
        } else {
          return {
            id: item,
            display: item,
          };
        }
      });
      const adaptedSmsArray = value.smsUsers.map((item: any) => {
        if (typeof item === 'object' && item !== null) {
          return {
            id: item.id,
            display: item.id,
          };
        } else {
          return {
            id: item,
            display: item,
          };
        }
      });
      dispatch(setIsClonedStatus(true));
      setIsRuleNameError({ duplicate: false, length: false });

      setCurrentRule((prevRule) => ({
        ...prevRule,
        ruleName: `${value.ruleName} - copy`,
        assets: selectedAssets,
        conditions: value.conditions,
        alertByEmail: value.alertByEmail,
        alertBySms: value.alertBySms,
        distributionUsers: adaptedArray,
        notificationLevel: value.notificationLevel,
        smsUsers: adaptedSmsArray,
      }));

      if (value.ruleId) {
        setSelectedRule(
          savedRules.find((item) => item.ruleId === value.ruleId)
        );
      }

      //  to add sensor info only while viewing the list
      const updatedListOptionsWithSensorData = selectedAssets?.map(
        (element) => {
          const deviceIdArr = [element.id]; // adding device id in array
          const tempRuleAssets = deviceIdArr.reduce((result: Asset[], item) => {
            const matchedDevice = assets.find(
              ({ device }) => device.deviceId === item
            );
            if (matchedDevice) {
              result.push(matchedDevice);
            }
            return result;
          }, []);
          const assetTags = getAssetsCommmonTags(tempRuleAssets);
          return {
            ...element,
            display: `${element.display} (${assetTags.length} sensors)`,
          };
        }
      );
      setAssetSelectOptions(updatedListOptionsWithSensorData);
      dispatch(
        updateNewRule({
          ruleName: `${value.ruleName} - copy`,
          assets: selectedAssets,
          conditions: value.conditions,
          alertByEmail: value.alertByEmail,
          alertBySms: value.alertBySms,
          alertByUI: value?.alertByUI ?? true,
          alertByNotificationCenter: value?.alertByNotificationCenter ?? true,
          distributionUsers: adaptedArray,
          notificationLevel: value.notificationLevel,
          smsUsers: adaptedSmsArray,
        })
      );
    }
  };

  return (
    <InteractiveModal
      initialState={open}
      title={title}
      handleAbort={handleAbort}
      SuccessButtonProps={{
        onClick: async () => {
          dispatch(updateNewRule(currentRule));
          handleConfirm();
        },
        disabled:
          Object.values(isRuleNameError).some((value) => value) ?? !isValidForm,
        label: t('next_conditions'),
      }}
      ModalContentProps={{
        gridItemProps: {
          lg: 12,
        },
      }}
      confirmCancellation
      enableDarkTheme={enableDarkTheme}
    >
      <Typography
        key="general-info-text"
        variant="medium"
        sx={{
          fontFamily: typography.fontFamily,
        }}
      >
        <RuleModalHeader
          disabledPages={[2, 3]}
          enableDarkTheme={enableDarkTheme}
        />
      </Typography>
      {/* Rule Name TextField */}

      <Box display={'flex'} flexDirection={'row'} gap={2} width={'100%'}>
        <Box display={'flex'} flexDirection={'column'} gap={2}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            gap={0}
            alignItems={'start'}
          >
            <Autocomplete
              freeSolo
              disablePortal
              size="small"
              options={savedRules}
              getOptionLabel={(option) => {
                return typeof option === 'object' ? option.ruleName : option;
              }}
              value={currentRule.ruleName}
              onChange={handleSelect}
              renderInput={(params) => (
                <SWTextField
                  name="ruleName"
                  label={t('rule_name')}
                  placeholder={t(
                    'enter_rule_name_or_select_from_existing_rule'
                  )}
                  TextFieldProps={{
                    ...params,
                    name: 'ruleName',
                    value: currentRule.ruleName,
                    onChange: handleChange,
                    onBlur: handleBlur,
                    InputProps: {
                      ...params.InputProps,
                      sx: {
                        width: '400px',
                        color: palette.textPrimary,
                        border: 'gray 1px solid',
                      },
                    },
                  }}
                  enableDarkTheme={enableDarkTheme}
                />
              )}
            />

            {Object.values(isRuleNameError).some((value) => value) ? (
              <Typography
                key="small-warning-text"
                variant="small"
                sx={{
                  paddingTop: '0px',
                  color: '#e57373',
                }}
                textAlign={'right'}
                fontSize={'10px'}
              >
                {isRuleNameError.length
                  ? 'Rule name cannot be more than 40 characters'
                  : isRuleNameError.duplicate
                  ? 'Another Rule currenlty uses this rule name.'
                  : 'Rule name error'}
              </Typography>
            ) : (
              <Typography
                key="small-info-text"
                variant="small"
                fontSize={'10px'}
                sx={{
                  paddingTop: '0px',
                  color: palette.textPrimary,
                }}
                textAlign={'right'}
              >
                {t(
                  'only_underscores_spaces_and_dashes_are_permitted_as_special_'
                )}
              </Typography>
            )}
          </Box>

          {/* Rule Type and Feature Type selectors for privileged users */}
          {isPrivilegedUser && (
            <Box display={'flex'} flexDirection={'column'} gap={2} mt={2}>
              <Box key="ruleType">
                <Typography
                  variant="bodyBold"
                  display="block"
                  mb={'8px'}
                  sx={{
                    color: palette.textPrimary,
                  }}
                >
                  {t('rule_type')}
                </Typography>
                <Selector
                  inputProps={{
                    sx: {
                      minWidth: '355px',
                    },
                  }}
                  value={currentRule.ruleType ?? 'user-defined'}
                  onChange={(event) => {
                    const newRuleType = event.target.value as string;
                    // When user-defined is selected, featureType is always sensor-based
                    const newFeatureType =
                      newRuleType === 'user-defined'
                        ? 'sensor-based'
                        : currentRule.featureType === 'sensor-based'
                        ? 'ml-insights'
                        : currentRule.featureType;
                    setCurrentRule({
                      ...currentRule,
                      ruleType: newRuleType,
                      featureType: newFeatureType,
                    });
                  }}
                  selectorOptions={ruleTypeOptions}
                  enableDarkTheme={enableDarkTheme}
                  height="20px"
                />
              </Box>
              <Box key="featureType">
                <Typography
                  variant="bodyBold"
                  display="block"
                  mb={'8px'}
                  sx={{
                    color: palette.textPrimary,
                  }}
                >
                  {t('feature_type')}
                </Typography>
                <Selector
                  inputProps={{
                    sx: {
                      minWidth: '355px',
                    },
                  }}
                  value={
                    currentRule.ruleType === 'user-defined'
                      ? 'sensor-based'
                      : currentRule.featureType ?? 'ml-insights'
                  }
                  onChange={(event) => {
                    setCurrentRule({
                      ...currentRule,
                      featureType: event.target.value as string,
                    });
                  }}
                  selectorOptions={
                    currentRule.ruleType === 'user-defined'
                      ? [{ id: 'sensor-based', display: 'Sensor Based' }]
                      : featureTypeOptions
                  }
                  enableDarkTheme={enableDarkTheme}
                  height="20px"
                  disabled={currentRule.ruleType === 'user-defined'}
                />
              </Box>
            </Box>
          )}

          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <Box key="assetType">
              <Typography
                variant="bodyBold"
                display="block"
                mb={'16px'}
                sx={{
                  color: palette.textPrimary,
                }}
              >
                {t('applies_to')}
              </Typography>

              {/* Asset Types */}
              <Selector
                inputProps={{
                  sx: {
                    minWidth: '355px',
                  },
                }}
                fallbackValue="Select asset type"
                value={selectedAssetDetailType(assetType, 'assetType')}
                onChange={(event) => {
                  const selectedAssetType = selectedAssetDetailType(
                    event.target.value,
                    'assetType'
                  );
                  setAssetFilter({
                    assetType: selectedAssetType,
                    assetMake: [],
                    assetModel: [],
                  });
                  // Reset existing selected assets
                  setCurrentRule({
                    ...currentRule,
                    assets: [],
                  });
                }}
                selectorOptions={assetTypeOptions}
                enableDarkTheme={enableDarkTheme}
                height="20px"
                formControlSx={{
                  height: '20px',
                }}
              />
            </Box>

            <Box key="assetMake">
              {/* Asset Make List */}
              <MultiSelector
                labelText="Select Asset Make/OEM"
                value={assetMake}
                renderValue={(selected) => {
                  return `${selected?.length || 0} OEM(s) Selected`;
                }}
                handleSelectOne={(event) => {
                  // Reset existing selected assets
                  setCurrentRule({
                    ...currentRule,
                    assets: [],
                  });

                  const updatedAssetFilter = {
                    ...assetFilter,
                    assetModel: [],
                  };

                  handleSelectSingle(
                    event,
                    updatedAssetFilter,
                    setAssetFilter,
                    'assetMake'
                  );
                }}
                handleSelectAll={() => {
                  // Reset existing selected assets
                  setCurrentRule({
                    ...currentRule,
                    assets: [],
                  });

                  const updatedAssetFilter = {
                    ...assetFilter,
                    assetModel: [],
                  };

                  handleSelectAll(
                    assetMakeOptions,
                    updatedAssetFilter,
                    setAssetFilter,
                    'assetMake'
                  );
                }}
                selectorOptions={assetMakeOptions}
                minWidth="400px"
                enableDarkTheme={enableDarkTheme}
                height={'20px'}
                size="small"
              />
            </Box>

            <Box key="AssetmodelList">
              {/* Asset Model List */}
              <MultiSelector
                labelText="Select Asset Model"
                value={assetFilter.assetModel}
                renderValue={(selected) => {
                  return `${selected?.length || 0} Model(s) Selected`;
                }}
                handleSelectOne={(event) => {
                  // Reset existing selected assets
                  setCurrentRule({
                    ...currentRule,
                    assets: [],
                  });

                  handleSelectSingleModel(
                    event,
                    assetFilter,
                    setAssetFilter,
                    'assetModel',
                    Infinity,
                    false,
                    model
                  );
                }}
                handleSelectAll={() => {
                  // Reset existing selected assets
                  setCurrentRule({
                    ...currentRule,
                    assets: [],
                  });

                  handleSelectAll(
                    assetModelOptions,
                    assetFilter,
                    setAssetFilter,
                    'assetModel'
                  );
                }}
                selectorOptions={assetModelOptions}
                minWidth="400px"
                enableDarkTheme={enableDarkTheme}
                height={'20px'}
                size="small"
              />
            </Box>

            <Box key="assetList">
              {/* Assets List */}
              <MultiSelector
                labelText="Select Asset List"
                value={currentRule.assets}
                renderValue={(selected) => {
                  return `${selected?.length || 0} Asset(s) Selected`;
                }}
                handleSelectOne={(event) => {
                  handleSelectAssetSingle(event, currentRule, setCurrentRule);
                }}
                handleSelectAll={() => {
                  handleSelectAssetAll(
                    assetSelectOptions,
                    currentRule,
                    setCurrentRule
                  );
                }}
                selectorOptions={assetSelectOptions
                  .slice() // Create a shallow copy of the array to avoid mutating the original
                  .sort((a, b) => a.display.localeCompare(b.display))}
                minWidth="400px"
                enableDarkTheme={enableDarkTheme}
                height={'20px'}
                size="small"
              />
            </Box>
          </Box>
        </Box>
        <Box
          width={'100%'}
          display={'flex'}
          alignItems={'end'}
          height={'100%'}
          marginTop={2}
        >
          <SensorInfoBox selectedAssets={currentRule.assets} assets={assets} />
        </Box>
      </Box>
    </InteractiveModal>
  );
};
